import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';

export interface CarbonCreditInterface {
  includeCarbonCredit: boolean;
  inputType: string;
  databasePriceCollection: string;
  databasePriceReference: string;
  databasePriceName: string;
  databaseTranslationId: string;
  customPrice: CarbonCreditCustomPrice;
  cpi: UnitValue;
}

export class CarbonCredit {
  includeCarbonCredit: boolean;
  inputType: string;
  databasePriceCollection: string;
  databasePriceReference: string;
  databasePriceName: string;
  databaseTranslationId: string;
  customPrice: CarbonCreditCustomPrice;
  cpi: UnitValue;

  constructor(param: CarbonCreditInterface = {
    includeCarbonCredit: true,
    inputType: '',
    databasePriceCollection: null,
    databasePriceReference: null,
    databasePriceName: null,
    databaseTranslationId: null,
    customPrice: new CarbonCreditCustomPrice(),
    cpi: new UnitValue()
  }) {
    this.includeCarbonCredit = param.includeCarbonCredit;
    this.inputType = param.inputType;
    this.databasePriceCollection = param.databasePriceCollection;
    this.databasePriceReference = param.databasePriceReference;
    this.databasePriceName = param.databasePriceName;
    this.databaseTranslationId = param.databaseTranslationId;
    this.customPrice = new CarbonCreditCustomPrice(param.customPrice);
    this.cpi = new UnitValue(param.cpi);
  }

  static deepCopy(carbonCredit: CarbonCredit) {
    let copy;
    if (carbonCredit) {
      copy = new CarbonCredit(carbonCredit);
      return copy;
    }
    throw new Error('Carbon credit copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, CarbonCreditPropName.inputType, this.inputType, [...path, 'inputType']);
    inputs = InputSummaryUtils.performPush(inputs, CarbonCreditPropName.databasePriceRef, this.getNameTranslationId(), [...path, 'databasePriceName']);
    if (this.inputType !== CarbonCreditInputType.Database) {
      inputs.push(...this.customPrice.getSummaryInputs([...path, 'customPrice']));
    }
    inputs = InputSummaryUtils.performPush(inputs, CarbonCreditPropName.cpi, this.cpi, [...path, 'cpi']);
    return inputs;
  }

  private getNameTranslationId() {
    return this.databaseTranslationId ? this.databaseTranslationId : this.databasePriceName;
  }
}

export enum CarbonCreditInputType {
  Database = 'PROJECT.GHG.INPUT_TYPE.DATABASE',
  File = 'PROJECT.GHG.INPUT_TYPE.FILE',
  Manual = 'PROJECT.GHG.INPUT_TYPE.MANUAL'
}

export enum CarbonCreditPropName {
  includeCarbonCredit = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT_PRICE.ENABLED',
  inputType = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT_PRICE.INPUT_TYPE',
  cpi = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT_PRICE.CPI',
  customPrice = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT_PRICE.CUSTOM_PRICE',
  databasePriceRef = 'INPUT_SUMMARY.GHG_EMISSIONS.CARBON_CREDIT_PRICE.DATABASE_PRESET',
  id_inputType = 'input type',
  id_cpi = 'CPI',
  id_customPrice = 'custom price',
  id_databasePriceRef = 'database preset',
}

export enum CarbonCredErrorMessage {
  MissingDbRef = 'PROJECT.GHG.ERR_DB_REF',
}

export interface CarbonCreditCustomPriceInterface {
  name: string;
  price: UnitValue;
  pricePer: UnitValue;
}

export class CarbonCreditCustomPrice {
  public name: string;
  public price: UnitValue;
  public pricePer: UnitValue;

  constructor(param: CarbonCreditCustomPriceInterface = {
    name: '',
    price: new UnitValue(),
    pricePer: new UnitValue()
  }) {
    this.name = param.name;
    this.price = new UnitValue(param.price);
    this.pricePer = new UnitValue(param.pricePer);
  }

  static deepCopy(ccCustomPrice: CarbonCreditCustomPrice) {
    let copy;
    if (ccCustomPrice) {
      copy = new CarbonCreditCustomPrice(ccCustomPrice);
      return copy;
    }
    throw new Error('Carbon credit custom price copy failed');
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, CustomPricePropName.price, this.price, [...path, 'price']);
    inputs = InputSummaryUtils.performPush(inputs, CustomPricePropName.pricePer, this.pricePer, [...path, 'pricePer']);
    return inputs;
  }
}

export enum CustomPricePropName {
  name = 'PROJECT.GHG.NAME',
  price = 'PROJECT.GHG.PRICE',
  pricePer = 'PROJECT.GHG.PRICE_PER'
}
